"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { TEN_GENERATION_CONTENT } from "./engine/contentPack";
import {
  LEGACY_LOGIC_SAVE_KEY,
  LOGIC_SAVE_KEY,
  activeHousehold,
  activePerson,
  advanceLogicYear,
  ageOf,
  createLogicGame,
  inheritAs,
  nameNewborns,
  resolveAdoptionNames,
  resolveLogicChoice,
} from "./engine/engine";
import { FAMILY_NAME_CANDIDATES, GIVEN_NAME_CANDIDATES } from "./engine/names";
import { familyRelationLabel, householdStatusLabel } from "./engine/relations";
import { deserializeLogicState, serializeLogicState } from "./engine/migration";
import type {
  AptitudeDomain,
  EventEffects,
  LogicGameState,
  PersonState,
  PersonalStats,
  RelationshipCandidate,
} from "./engine/types";

const STAT_LABELS: Record<keyof PersonalStats, string> = {
  stamina: "體力",
  intellect: "智力",
  charm: "魅力",
  empathy: "共感",
  luck: "運氣",
  health: "健康",
};

const RELATIONSHIP_LABELS = { single: "單身", dating: "交往中", married: "已婚" };
type GameTab = "life" | "family" | "assets" | "history" | "manage";
const GAME_TABS: Array<{ id: GameTab; label: string }> = [
  { id: "life", label: "人生" },
  { id: "family", label: "家庭" },
  { id: "assets", label: "資產" },
  { id: "history", label: "歷程" },
  { id: "manage", label: "管理" },
];
const APTITUDE_LABELS: Record<AptitudeDomain, string> = {
  science: "科學技術",
  language: "語言人文",
  business: "商業領導",
  creative: "藝術創作",
  sports: "運動體能",
  care: "社會照護",
  practical: "工藝實作",
  nature: "自然觀察",
};

function genderText(gender: PersonState["gender"]) {
  return gender === "female" ? "女" : "男";
}

function careerText(person: PersonState, year: number) {
  if (person.career.status === "retired") return `退休（曾任${person.career.occupation}）`;
  if (person.career.status === "unemployed") return "暫時待業";
  if (person.career.status === "student") return ageOf(person, year) < 18 ? "成長與就學中" : "學生";
  return `${person.career.industry}・${person.career.occupation}`;
}

function generationGroups(state: LogicGameState) {
  const groups = new Map<number, PersonState[]>();
  Object.values(state.people).forEach((person) => {
    if (person.flags.includes("former-partner-hidden") && person.id !== state.activePersonId) return;
    const generation = state.dynasty.generationByPersonId[person.id] ?? 0;
    groups.set(generation, [...(groups.get(generation) || []), person]);
  });
  return [...groups.entries()].sort(([left], [right]) => left - right);
}

function signed(value: number) {
  return `${value > 0 ? "+" : ""}${value}`;
}

function effectParts(effects: EventEffects, scale = 1) {
  const parts: string[] = [];
  if (effects.assets) parts.push(`家庭資金 ${signed(Math.round(effects.assets * scale))}`);
  if (effects.stats) {
    (Object.keys(effects.stats) as Array<keyof PersonalStats>).forEach((key) => {
      const value = Math.round(Number(effects.stats?.[key] || 0) * scale);
      if (value) parts.push(`${STAT_LABELS[key]} ${signed(value)}`);
    });
  }
  if (effects.career?.industry) parts.push(`業界改為 ${effects.career.industry}`);
  if (effects.career?.occupation) parts.push(`職種改為 ${effects.career.occupation}`);
  if (effects.career?.status) parts.push(`工作狀態：${effects.career.status === "self-employed" ? "自營業" : effects.career.status === "employed" ? "就業" : effects.career.status}`);
  if (effects.career?.annualIncome !== undefined) parts.push(`年收入設為 ${effects.career.annualIncome}`);
  if (effects.careerIncomeDelta) parts.push(`年收入 ${signed(effects.careerIncomeDelta)}（下年度起）`);
  if (effects.careerLevelDelta) parts.push(`職級 ${signed(effects.careerLevelDelta)}`);
  if (effects.careerPauseYears) parts.push(`收入中斷 ${effects.careerPauseYears} 年`);
  if (effects.investmentLevelDelta) parts.push(`投資等級 ${signed(effects.investmentLevelDelta)}`);
  if (effects.businessLevelDelta) parts.push(`家業等級 ${signed(effects.businessLevelDelta)}`);
  if (effects.interests?.length) parts.push(`興趣：${effects.interests.map((domain) => APTITUDE_LABELS[domain]).join("、")}`);
  if (effects.discoverTalents?.length) parts.push(`發現天賦：${effects.discoverTalents.map((domain) => APTITUDE_LABELS[domain]).join("、")}`);
  if (effects.talentDelta) {
    for (const [domain, raw] of Object.entries(effects.talentDelta)) {
      const value = Number(raw || 0);
      if (value) parts.push(`${APTITUDE_LABELS[domain as AptitudeDomain]}潛能 ${signed(value)}`);
    }
  }
  if (effects.educationYears) parts.push(`繼續就學 ${effects.educationYears} 年`);
  if (effects.traits?.length) parts.push(`留下特質：${effects.traits.join("、")}`);
  if (effects.action === "marry-partner") parts.push("伴侶加入共同家庭戶");
  if (effects.action === "end-relationship") parts.push("關係結束");
  if (effects.action === "adopt-child") parts.push("領養一名孩子");
  if (effects.action === "protect-pregnancy") parts.push("懷孕者下一完整年度收入 0・生產成功率 100%");
  if (effects.action === "reduce-work-pregnancy") parts.push("懷孕者下一完整年度收入 50%・生產成功率 85%");
  if (effects.action === "continue-pregnancy") parts.push("懷孕者收入不變・生產成功率 50%");
  if (effects.action === "end-pregnancy") parts.push("這次懷孕結束・懷孕者共感 -2・健康 -3");
  if (effects.action === "accept-adoption") parts.push("接受全部孩子並查看個別資料");
  if (effects.action === "decline-adoption") parts.push("不設冷卻，未來仍可再遇到");
  if (effects.action === "adoption-care-both") parts.push("兩位照顧者：收入 0・共感 +5・健康 +2");
  if (effects.action === "adoption-care-primary") parts.push("下一步選擇一位主要照顧者");
  if (effects.action === "adoption-care-balance") parts.push("兩位照顧者：收入 60%・共感 +5・魅力 +3・健康 -1");
  if (effects.action === "adoption-care-external") parts.push("兩位照顧者：共感 -2・家庭依照顧期間每年 -100");
  if (effects.action === "adoption-primary-actor") parts.push("主角：收入 0・共感 +2・魅力 -2・健康 +1");
  if (effects.action === "adoption-primary-partner") parts.push("伴侶：收入 0・共感 +2・魅力 -2・健康 +1");
  return parts;
}

function secureIndex(length: number) {
  const values = new Uint32Array(1);
  globalThis.crypto.getRandomValues(values);
  return values[0] % length;
}

function secureSeed() {
  const values = new Uint32Array(2);
  globalThis.crypto.getRandomValues(values);
  return `${Date.now()}:${values[0]}:${values[1]}`;
}

function differentRandom(values: string[], current: string) {
  const alternatives = values.filter((value) => value !== current);
  return alternatives[secureIndex(alternatives.length)] || values[secureIndex(values.length)];
}

function candidateCareer(candidate: RelationshipCandidate) {
  return candidate.career.status === "student"
    ? "學生"
    : `${candidate.career.industry}・${candidate.career.occupation}（年收入 ${candidate.career.annualIncome}）`;
}

function saveDownload(state: LogicGameState) {
  const blob = new Blob([serializeLogicState(state)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `${state.dynasty.familyName}家-十代人生記存檔.json`;
  anchor.click();
  URL.revokeObjectURL(url);
}

async function endingImage(state: LogicGameState, person: PersonState) {
  const canvas = document.createElement("canvas");
  canvas.width = 1080;
  canvas.height = 1350;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Canvas unavailable");
  context.fillStyle = "#f4f1e9";
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.fillStyle = "#315947";
  context.fillRect(0, 0, canvas.width, 190);
  context.fillStyle = "#ffffff";
  context.font = "700 64px sans-serif";
  context.fillText("十代人生記", 72, 116);
  context.fillStyle = "#24362d";
  context.font = "700 54px sans-serif";
  context.fillText(`${person.name} 的人生結算`, 72, 290);
  context.font = "36px sans-serif";
  const lines = [
    `第 ${state.world.generationArc} 代・${ageOf(person, state.world.year)} 歲`,
    `職涯：${careerText(person, state.world.year)}`,
    `家庭資金：${activeHousehold(state).assets}`,
    `子女：${person.childIds.length} 人`,
    `完成世代：${state.completedGenerations + 1} 代`,
  ];
  lines.forEach((line, index) => context.fillText(line, 72, 400 + index * 78));
  context.font = "30px sans-serif";
  context.fillStyle = "#5c6c63";
  state.logs.slice(-6).forEach((log, index) => context.fillText(`${log.year}・${log.title}`.slice(0, 28), 72, 850 + index * 62));
  return new Promise<Blob>((resolve, reject) => canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error("Image export failed")), "image/png"));
}

export function LogicGameClient() {
  const [state, setState] = useState<LogicGameState | null>(null);
  const [hydrated, setHydrated] = useState(false);
  const [familyName, setFamilyName] = useState(() => FAMILY_NAME_CANDIDATES[secureIndex(FAMILY_NAME_CANDIDATES.length)]);
  const [givenName, setGivenName] = useState(() => GIVEN_NAME_CANDIDATES.female[secureIndex(GIVEN_NAME_CANDIDATES.female.length)]);
  const [gender, setGender] = useState<PersonState["gender"]>("female");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [ledgerOpen, setLedgerOpen] = useState(false);
  const [newbornNames, setNewbornNames] = useState<Record<string, string>>({});
  const [adoptionRenameOpen, setAdoptionRenameOpen] = useState(false);
  const [adoptionNames, setAdoptionNames] = useState<Record<string, { familyName: string; givenName: string }>>({});
  const [nameError, setNameError] = useState("");
  const [activeTab, setActiveTab] = useState<GameTab>("life");
  const importRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const saved = window.localStorage.getItem(LOGIC_SAVE_KEY) || window.localStorage.getItem(LEGACY_LOGIC_SAVE_KEY);
      if (saved) {
        try {
          setState(deserializeLogicState(saved));
        } catch {
          window.localStorage.removeItem(LOGIC_SAVE_KEY);
        }
      }
      setHydrated(true);
    }, 0);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!hydrated || !state) return;
    window.localStorage.setItem(LOGIC_SAVE_KEY, serializeLogicState(state));
  }, [hydrated, state]);

  const person = state ? activePerson(state) : null;
  const household = state ? activeHousehold(state) : null;
  const pendingEvent = state?.pendingChoice
    ? TEN_GENERATION_CONTENT.events.find((event) => event.id === state.pendingChoice?.eventId)
    : undefined;
  const pendingTarget = state?.pendingChoice?.targetPersonId ? state.people[state.pendingChoice.targetPersonId] : undefined;
  const encounterCandidate = state?.pendingChoice?.candidates ? Object.values(state.pendingChoice.candidates)[0] : undefined;
  const selected = state && selectedId ? state.people[selectedId] : undefined;
  const groups = useMemo(() => state ? generationGroups(state) : [], [state]);
  const randomizeSetupName = () => {
    setFamilyName((current) => differentRandom(FAMILY_NAME_CANDIDATES, current));
    setGivenName((current) => differentRandom(GIVEN_NAME_CANDIDATES[gender], current));
  };

  if (!hydrated) return <main className="life-app-loading" aria-busy="true">讀取人生存檔中…</main>;

  if (!state || !person || !household) {
    return (
      <main className="setup-stage">
        <img className="setup-art" src="/timeless-life-journal-watercolor.webp" alt="" />
        <section className="setup-card">
          <small>家族人生模擬</small>
          <h1>開始一段十代人生</h1>
          <p>替這一代命名，從出生開始寫下家族的生活。</p>
          <label>家族姓氏<input value={familyName} maxLength={8} onChange={(event) => setFamilyName(event.target.value)} /></label>
          <label>主角名字<input value={givenName} maxLength={8} onChange={(event) => setGivenName(event.target.value)} /></label>
          <fieldset>
            <legend>主角性別</legend>
            <div className="segmented-control">
              <button type="button" className={gender === "female" ? "active" : ""} onClick={() => { setGender("female"); setGivenName(GIVEN_NAME_CANDIDATES.female[secureIndex(GIVEN_NAME_CANDIDATES.female.length)]); }}>女</button>
              <button type="button" className={gender === "male" ? "active" : ""} onClick={() => { setGender("male"); setGivenName(GIVEN_NAME_CANDIDATES.male[secureIndex(GIVEN_NAME_CANDIDATES.male.length)]); }}>男</button>
            </div>
          </fieldset>
          <button type="button" className="small-button setup-random" onClick={randomizeSetupName}>換一組候選名字</button>
          <button
            type="button"
            className="primary-button"
            disabled={!familyName.trim() || !givenName.trim()}
            onClick={() => {
              const cleanFamily = familyName.trim();
              const cleanGiven = givenName.trim();
              const next = createLogicGame(`${secureSeed()}:${cleanFamily}:${cleanGiven}`, cleanFamily, `${cleanFamily}${cleanGiven}`);
              next.people[next.activePersonId].gender = gender;
              setState(next);
              setActiveTab("life");
            }}
          >開始人生</button>
        </section>
      </main>
    );
  }

  const age = ageOf(person, state.world.year);
  const livingHeirs = person.childIds.map((id) => state.people[id]).filter((child) => child && !child.deathYear);
  const partner = person.currentPartnerId ? state.people[person.currentPartnerId] : undefined;
  const eventText = pendingEvent?.text
    .replaceAll("{relative}", pendingTarget?.name || "家人")
    .replaceAll("{partner}", partner?.name || "交往對象");
  const latestLog = state.logs.at(-1);
  const nextStep = state.newbornIdsAwaitingName.length
    ? "替新生兒命名"
    : state.adoptionNameDecision
      ? "確認領養兒童姓名"
    : state.pendingChoice
      ? "完成目前選擇"
      : person.deathYear
        ? "查看結算與傳承"
        : "推進下一年";

  const shareEnding = async () => {
    const blob = await endingImage(state, person);
    const file = new File([blob], `${person.name}-人生結算.png`, { type: "image/png" });
    if (navigator.canShare?.({ files: [file] })) {
      await navigator.share({ files: [file], title: `${person.name}的人生結算` });
      return;
    }
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = file.name;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <main className="life-app-shell">
      <div className="life-app">
        <header className="life-hud">
          <div className="life-brand"><small>家族人生模擬</small><strong>十代人生記</strong></div>
          <div className="life-identity"><span>第 {state.world.generationArc} / 10 代</span><b>{age} 歲</b></div>
        </header>

        <div className="life-scroll" data-active-tab={activeTab}>
          {activeTab === "life" ? (
            <>
              <section className="watercolor-scene" aria-label="人生場景">
                <img src="/timeless-life-journal-watercolor.webp" alt="雨後庭院旁攤開的人生手帳" />
                <div className="scene-shade" />
                <div className="scene-meta"><span>{state.world.year}・{state.world.era}</span><strong>{person.name}</strong><span>{RELATIONSHIP_LABELS[person.relationshipStage]}</span></div>
                <div className="scene-figures"><span>{careerText(person, state.world.year)}</span><b>共同資金 {household.assets}</b></div>
              </section>

              <section className="life-story-sheet">
                <div className="stat-ribbon" aria-label="主角個人能力">
                  {(Object.keys(STAT_LABELS) as Array<keyof PersonalStats>).map((key) => (
                    <span key={key}><small>{STAT_LABELS[key]}</small><strong>{person.stats[key]}</strong></span>
                  ))}
                </div>

                {pendingEvent && state.pendingChoice && !state.newbornIdsAwaitingName.length && !state.adoptionNameDecision ? (
                  <section className="decision-flow" aria-label="目前唯一待處理選擇">
                    <small className="section-kicker">今年的選擇{pendingTarget ? `・${pendingTarget.name}` : ""}</small>
                    <h2>{state.pendingChoice.displayTitle || (pendingTarget ? `替 ${pendingTarget.name} 決定：${pendingEvent.title}` : pendingEvent.title)}</h2>
                    <p>{state.pendingChoice.displayText || eventText}</p>
                    {encounterCandidate ? (
                      <div className="encounter-person">
                        <strong>{encounterCandidate.name}・{genderText(encounterCandidate.gender)}・{state.world.year - encounterCandidate.birthYear} 歲</strong>
                        <span>{candidateCareer(encounterCandidate)}</span>
                        <span>魅力 {encounterCandidate.stats.charm}・共感 {encounterCandidate.stats.empathy}・健康 {encounterCandidate.stats.health}</span>
                      </div>
                    ) : null}
                    <div className={`compact-choice-grid choices-${pendingEvent.choices.length}`}>
                      {pendingEvent.choices.map((choice) => {
                        const available = state.pendingChoice?.validChoiceIds.includes(choice.id) ?? false;
                        const educationScale = state.pendingChoice?.adoptionEducation ? state.pendingChoice.educationEffectScale || 1 : 1;
                        const standardEffects = effectParts(choice.effects);
                        const ownEffects = educationScale === 1 ? standardEffects : effectParts(choice.effects, educationScale);
                        const targetEffects = choice.targetEffects ? effectParts(choice.targetEffects) : [];
                        const unavailableReason = choice.requires?.householdAssetsMin && household.assets < choice.requires.householdAssetsMin
                          ? `需要至少 ${choice.requires.householdAssetsMin} 家庭資金`
                          : choice.requires?.canConceive
                            ? "目前伴侶組合或年齡不符合生育條件"
                            : choice.requires?.description || "目前條件不符合";
                        return (
                          <button type="button" key={choice.id} disabled={!available} onClick={() => setState((current) => current ? resolveLogicChoice(current, choice.id) : current)}>
                            <strong>{choice.label}</strong>
                            <span>{state.pendingChoice?.adoptionEducation
                              ? `${choice.text.split("；")[0]}；費用與能力變化按剩餘學年 ${Math.round(educationScale * 100)}% 計算。`
                              : choice.text}</span>
                            <small className={available ? "choice-result" : "choice-result unavailable"}>
                              {available
                                ? [
                                    ...ownEffects,
                                    ...(targetEffects.length ? [`${pendingTarget?.name || "對方"}：${targetEffects.join("、")}`] : []),
                                    ...(choice.followUp ? ["之後會有後續結果"] : []),
                                  ].join("・") || "不會立即改變數值"
                                : unavailableReason}
                            </small>
                          </button>
                        );
                      })}
                    </div>
                  </section>
                ) : person.deathYear ? (
                  <section className="life-ending">
                    <small className="section-kicker">第 {state.world.generationArc} 代人生結算</small>
                    <h2>{person.name} 的人生告一段落</h2>
                    <p>{age} 歲・{careerText(person, state.world.year)}・子女 {person.childIds.length} 人・家庭資金 {household.assets}</p>
                    <button type="button" className="secondary-button" onClick={shareEnding}>分享／儲存結算圖</button>
                    {state.world.generationArc < 10 && livingHeirs.length ? (
                      <div className="heir-list"><strong>選擇下一代主角</strong>{livingHeirs.map((heir) => (
                        <button type="button" key={heir.id} onClick={() => setState((current) => current ? inheritAs(current, heir.id) : current)}>{heir.name}・{ageOf(heir, state.world.year)} 歲・{careerText(heir, state.world.year)}</button>
                      ))}</div>
                    ) : <p>{state.world.generationArc >= 10 ? "十代家族歷程已完成。" : "本代沒有可繼承的子女。"}</p>}
                  </section>
                ) : (
                  <section className="year-summary">
                    <small className="section-kicker">最近紀錄</small>
                    <h2>{latestLog?.title || `${person.name} 的人生`}</h2>
                    <p>{latestLog?.text || "新的一年正等待你的決定。"}</p>
                    <div className="advance-row"><span>{nextStep}</span><button type="button" className="advance-button" disabled={Boolean(state.newbornIdsAwaitingName.length || state.adoptionNameDecision)} onClick={() => setState((current) => current ? advanceLogicYear(current) : current)}>下一年</button></div>
                  </section>
                )}
              </section>
            </>
          ) : null}

          {activeTab === "family" ? (
            <section className="tab-page family-tab">
              <header><small>家族關係圖</small><h2>{state.dynasty.familyName}家的十代紀錄</h2><p>點選名字查看能力、教育、職涯與家庭狀態。</p></header>
              <div className="family-tree-list">
                {groups.map(([generation, members]) => (
                  <div className="generation-row" key={generation}>
                    <span>第 {generation} 代</span>
                    <div className="logic-member-grid">
                      {members.map((member) => (
                        <button type="button" key={member.id} className={`${member.gender} ${member.id === state.activePersonId ? "current" : ""}`} onClick={() => setSelectedId(member.id)}>
                          <small>{familyRelationLabel(state, member)}・{householdStatusLabel(state, member)}</small>
                          <strong>{member.name}</strong>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ) : null}

          {activeTab === "assets" ? (
            <section className="tab-page assets-tab">
              <header><small>共同家庭戶</small><h2>{household.name}</h2><p>同戶收入與支出全部進入同一份家庭帳本。</p></header>
              <div className="asset-summary"><span>共同資金<strong>{household.assets}</strong></span><span>年度淨額<strong className={household.annualLedger.net >= 0 ? "positive" : "negative"}>{signed(household.annualLedger.net)}</strong></span></div>
              <dl className="compact-facts"><div><dt>同住</dt><dd>{household.memberIds.length} 人</dd></div><div><dt>扶養</dt><dd>{household.dependentIds.length} 人</dd></div><div><dt>投資</dt><dd>{household.investmentLevel} 級</dd></div><div><dt>家業</dt><dd>{household.businessLevel} 級</dd></div></dl>
              <section className="career-summary"><small>主角職涯</small><h3>{careerText(person, state.world.year)}</h3><p>職級 {person.career.level}・年資 {person.career.experience}・年收入 {person.career.annualIncome}</p></section>
              <button type="button" className="ledger-toggle" onClick={() => setLedgerOpen((open) => !open)}>{ledgerOpen ? "收起年度明細" : `查看 ${household.annualLedger.year} 年收支明細`}</button>
              {ledgerOpen ? <div className="ledger-details"><strong>收入 {household.annualLedger.income}・支出 {household.annualLedger.expenses}</strong>{household.annualLedger.details.map((detail, index) => <span key={`${detail}-${index}`}>{detail}</span>)}</div> : null}
              <section className="member-income-list"><small>同戶成員狀態</small>{household.memberIds.map((id) => state.people[id]).filter(Boolean).map((member) => <button type="button" key={member.id} onClick={() => setSelectedId(member.id)}><span>{member.name}<small>{careerText(member, state.world.year)}</small></span><strong>年收 {member.career.annualIncome}</strong></button>)}</section>
            </section>
          ) : null}

          {activeTab === "history" ? (
            <section className="tab-page history-tab"><header><small>人生歷程</small><h2>最近的家族紀錄</h2></header><div className="timeline-list">{state.logs.slice().reverse().map((log, index) => <article key={`${log.year}-${index}`}><small>{log.year} 年</small><strong>{log.title}</strong><p>{log.text}</p></article>)}</div></section>
          ) : null}

          {activeTab === "manage" ? (
            <section className="tab-page manage-tab">
              <header><small>存檔與遊戲</small><h2>管理目前人生</h2><p>遊戲會自動保存在這台裝置，也可匯出檔案留存。</p></header>
              <button type="button" className="manage-action" onClick={() => saveDownload(state)}>匯出存檔<span>下載可再次匯入的 JSON 存檔</span></button>
              <button type="button" className="manage-action" onClick={() => importRef.current?.click()}>匯入存檔<span>讀取先前匯出的十代人生記存檔</span></button>
              <input ref={importRef} hidden type="file" accept="application/json,.json" onChange={async (event) => { const file = event.target.files?.[0]; if (!file) return; try { setState(deserializeLogicState(await file.text())); setActiveTab("life"); } catch { window.alert("這不是可讀取的十代人生記存檔。"); } event.target.value = ""; }} />
              <button type="button" className="manage-action danger" onClick={() => { if (window.confirm("確定清除目前存檔並重新開始嗎？")) { window.localStorage.removeItem(LOGIC_SAVE_KEY); randomizeSetupName(); setState(null); setActiveTab("life"); } }}>重新開始<span>清除這台裝置上的目前人生</span></button>
            </section>
          ) : null}
        </div>

        <nav className="life-tabs" aria-label="主要功能">
          {GAME_TABS.map((tab) => <button type="button" key={tab.id} className={activeTab === tab.id ? "active" : ""} aria-current={activeTab === tab.id ? "page" : undefined} onClick={() => setActiveTab(tab.id)}><span />{tab.label}</button>)}
        </nav>
      </div>

      {state.adoptionNameDecision ? (
        <div className="modal-backdrop" role="presentation">
          <section className="person-modal newborn-modal" role="dialog" aria-modal="true">
            <small>領養兒童姓名確認</small>
            <h2>{state.adoptionNameDecision.childIds.length === 1
              ? "一名孩子即將加入家庭"
              : `${state.adoptionNameDecision.childIds.length} 名孩子即將一起加入家庭`}</h2>
            <p>以下是孩子目前使用的原姓名。你可以全部保留，也可以進入欄位後逐一決定；沒有修改的欄位會維持原樣。</p>
            {state.adoptionNameDecision.childIds.map((id, index) => {
              const child = state.people[id];
              const original = state.adoptionNameDecision?.originalNames[id];
              if (!child || !original) return null;
              const input = adoptionNames[id] || original;
              return adoptionRenameOpen ? (
                <div className="adoption-name-row" key={id}>
                  <strong>第 {index + 1} 名・{genderText(child.gender)}・{ageOf(child, state.world.year)} 歲（原姓名：{original.familyName}{original.givenName}）</strong>
                  <label>姓氏<input value={input.familyName} maxLength={8} onChange={(event) => setAdoptionNames((current) => ({ ...current, [id]: { ...input, familyName: event.target.value } }))} /></label>
                  <label>名字<input value={input.givenName} maxLength={8} onChange={(event) => setAdoptionNames((current) => ({ ...current, [id]: { ...input, givenName: event.target.value } }))} /></label>
                </div>
              ) : (
                <p key={id}><strong>{original.familyName}{original.givenName}</strong>・{genderText(child.gender)}・{ageOf(child, state.world.year)} 歲</p>
              );
            })}
            {nameError ? <p className="form-error">{nameError}</p> : null}
            {adoptionRenameOpen ? (
              <button className="primary-button" onClick={() => {
                try {
                  setState((current) => current ? resolveAdoptionNames(current, "change", adoptionNames) : current);
                  setAdoptionRenameOpen(false);
                  setAdoptionNames({});
                  setNameError("");
                } catch (error) { setNameError(error instanceof Error ? error.message : "請確認姓名"); }
              }}>確認所有姓名</button>
            ) : (
              <div className="adoption-name-actions">
                <button className="secondary-button" onClick={() => {
                  try {
                    setState((current) => current ? resolveAdoptionNames(current, "keep") : current);
                    setNameError("");
                  } catch (error) { setNameError(error instanceof Error ? error.message : "請確認姓名"); }
                }}>保留原姓名</button>
                <button className="primary-button" onClick={() => {
                  setAdoptionNames(Object.fromEntries(state.adoptionNameDecision!.childIds.map((id) => [id, { ...state.adoptionNameDecision!.originalNames[id] }])));
                  setAdoptionRenameOpen(true);
                  setNameError("");
                }}>更改姓名</button>
              </div>
            )}
          </section>
        </div>
      ) : null}

      {state.newbornIdsAwaitingName.length ? (
        <div className="modal-backdrop" role="presentation">
          <section className="person-modal newborn-modal" role="dialog" aria-modal="true">
            <small>新生兒命名</small><h2>{state.newbornIdsAwaitingName.length > 1 ? `迎接 ${state.newbornIdsAwaitingName.length} 名孩子` : "迎接孩子"}</h2>
            <p>性別已由事件決定。請分別輸入名字，所有欄位會一次儲存。</p>
            {state.newbornIdsAwaitingName.map((id, index) => {
              const child = state.people[id];
              return <label key={id}>第 {index + 1} 名・{genderText(child.gender)}寶寶<input value={newbornNames[id] ?? child.name.replace(state.dynasty.familyName, "")} maxLength={8} onChange={(event) => setNewbornNames((current) => ({ ...current, [id]: event.target.value }))} /></label>;
            })}
            {nameError ? <p className="form-error">{nameError}</p> : null}
            <button className="primary-button" onClick={() => {
              try {
                setState((current) => {
                  if (!current) return current;
                  const resolvedNames = Object.fromEntries(current.newbornIdsAwaitingName.map((id) => [
                    id,
                    newbornNames[id] ?? current.people[id]?.name.replace(current.dynasty.familyName, "") ?? "",
                  ]));
                  return nameNewborns(current, resolvedNames);
                });
                setNewbornNames({});
                setNameError("");
              }
              catch (error) { setNameError(error instanceof Error ? error.message : "請確認名字"); }
            }}>儲存所有名字</button>
          </section>
        </div>
      ) : null}

      {selected ? (
        <div className="modal-backdrop" role="presentation" onClick={() => setSelectedId(null)}>
          <section className="person-modal" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
            <small>{familyRelationLabel(state, selected)}・{householdStatusLabel(state, selected)}</small><h2>{selected.name}</h2>
            <div className="person-facts">
              <span>性別：{genderText(selected.gender)}</span><span>年齡：{ageOf(selected, state.world.year)} 歲</span>
              <span>家庭戶：{state.households[selected.householdId]?.name}</span><span>感情：{RELATIONSHIP_LABELS[selected.relationshipStage]}</span>
              <span>職涯：{careerText(selected, state.world.year)}</span><span>職級：{selected.career.level}</span>
              <span>年收入：{selected.career.annualIncome}</span><span>狀態：{selected.deathYear ? `${selected.deathYear} 年離世` : "生活中"}</span>
            </div>
            <div className="detail-stats">{(Object.keys(STAT_LABELS) as Array<keyof PersonalStats>).map((key) => <span key={key}>{STAT_LABELS[key]} <strong>{selected.stats[key]}</strong></span>)}</div>
            <p>興趣：{selected.interests.length ? selected.interests.map((domain) => APTITUDE_LABELS[domain]).join("、") : "仍在探索"}</p>
            <p>已發現天賦：{selected.discoveredTalents.length
              ? selected.discoveredTalents.map((domain) => `${APTITUDE_LABELS[domain]} ${selected.talents[domain] ?? 0}`).join("、")
              : "尚未明顯展現"}</p>
            {selected.longTermStates.careerAffinity ? <p>目前職涯適配：{selected.longTermStates.careerAffinity}</p> : null}
            <p>特質：{selected.traits.length ? selected.traits.join("、") : "尚未形成"}</p>
            <p>教育：{selected.education.length ? selected.education.map((record) => record.stage).join("、") : "尚無紀錄"}</p>
            <button className="primary-button" onClick={() => setSelectedId(null)}>關閉</button>
          </section>
        </div>
      ) : null}
    </main>
  );
}
